import { styled } from '@mui/material/styles';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const ink = '#1a1a2e';
const muted = '#5a6478';
const headingFont = "'Plus Jakarta Sans', sans-serif";

/* ─── Intro ─── */
export const IntroPanel = styled('section')({
  marginTop: 12,
  padding: '48px 52px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.05fr) minmax(280px, 0.95fr)',
  gap: '8px 48px',
  alignItems: 'center',
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    gap: 20,
    padding: '36px 28px',
  },
  '@media (max-width: 600px)': {
    borderRadius: 18,
    padding: '26px 18px 24px',
  },
});

export const IntroCopy = styled('div')({
  minWidth: 0,
});

export const Eyebrow = styled('p')({
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
});

export const IntroHeading = styled('h1')({
  fontFamily: headingFont,
  fontSize: '2.6rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 12px',
  '@media (max-width: 900px)': {
    fontSize: '2.2rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
});

export const IntroLead = styled('p')({
  fontSize: '1.1rem',
  lineHeight: 1.55,
  color: muted,
  margin: 0,
  maxWidth: 520,
  '@media (max-width: 900px)': {
    maxWidth: 620,
  },
  '@media (max-width: 600px)': {
    fontSize: '1rem',
  },
});

export const StoryCard = styled('p')({
  margin: 0,
  padding: '18px 22px',
  borderRadius: 16,
  borderLeft: `4px solid ${blue}`,
  background: '#ffffff',
  boxShadow: '0 4px 16px rgba(26, 26, 46, 0.05)',
  fontSize: '1rem',
  lineHeight: 1.65,
  color: ink,
});

/* ─── Shared section heading ─── */
export const Section = styled('section')({
  marginTop: 40,
  textAlign: 'left',
  '@media (max-width: 600px)': {
    marginTop: 28,
  },
});

export const SectionTitle = styled('h2')({
  fontFamily: headingFont,
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 18px',
  '@media (max-width: 600px)': {
    fontSize: '1.4rem',
  },
});

/* ─── How we're different ─── */
export const DifferenceGrid = styled('ul')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 16,
  '@media (max-width: 1000px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  '@media (max-width: 560px)': {
    gridTemplateColumns: '1fr',
    gap: 12,
  },
});

export const DifferenceCard = styled('li')({
  padding: '20px 20px 22px',
  borderRadius: 16,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  boxShadow: '0 4px 16px rgba(26, 26, 46, 0.05)',
});

export const IconChip = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  marginBottom: 12,
  borderRadius: '50%',
  color: blue,
  background: 'rgba(1, 235, 178, 0.18)',
  '& svg': { fontSize: 22 },
});

export const CardTitle = styled('h3')({
  fontSize: '1.05rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 4px',
});

export const CardText = styled('p')({
  fontSize: '0.94rem',
  lineHeight: 1.55,
  color: muted,
  margin: 0,
});

/* ─── Founders ─── */
export const FounderGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 20,
  '@media (max-width: 860px)': {
    gridTemplateColumns: '1fr',
  },
});

export const FounderCard = styled('article')({
  padding: 24,
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  boxShadow: '0 4px 16px rgba(26, 26, 46, 0.05)',
  '@media (max-width: 600px)': {
    padding: 18,
  },
});

export const FounderHeader = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 14,
});

export const FounderImage = styled('img')({
  flex: '0 0 auto',
  width: 96,
  height: 96,
  objectFit: 'cover',
  borderRadius: 16,
  background: MINT_MIST,
});

export const FounderName = styled('h3')({
  fontFamily: headingFont,
  fontSize: '1.25rem',
  fontWeight: 700,
  color: ink,
  margin: 0,
});

export const FounderRole = styled('p')({
  fontSize: '0.92rem',
  fontWeight: 600,
  color: blue,
  margin: '2px 0 6px',
});

export const SocialLinks = styled('div')({
  display: 'flex',
  gap: 4,
  marginLeft: -8,
  '& .MuiIconButton-root': {
    color: muted,
    '&:hover': { color: blue, background: 'rgba(13, 78, 254, 0.06)' },
  },
});

export const FounderBio = styled('p')({
  fontSize: '0.98rem',
  lineHeight: 1.65,
  color: muted,
  margin: 0,
});

/* ─── Closing contact line ─── */
export const ContactLine = styled('p')({
  margin: '28px 0 48px',
  fontSize: '1rem',
  color: muted,
  textAlign: 'center',
  '& a': {
    fontWeight: 600,
    color: blue,
    textDecorationColor: 'rgba(13, 78, 254, 0.35)',
    textUnderlineOffset: 3,
  },
  '@media (max-width: 600px)': {
    margin: '22px 0 32px',
  },
});
